/**
 * HistoryTimeline Component
 * Visualizes listening history as a 30-day timeline with navigation
 */

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import type { PlayHistory } from '@/api/types';

interface HistoryTimelineProps {
  items: PlayHistory[];
  onDateSelect: (date: Date | null) => void;
  selectedDate: Date | null;
}

interface DayData {
  date: Date;
  count: number;
  dateString: string; // YYYY-MM-DD format
}

/**
 * Get YYYY-MM-DD string from Date in LOCAL timezone (not UTC)
 * This ensures consistent date comparison regardless of timezone
 */
function getLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Groups play history items by day and counts tracks per day
 */
function groupByDay(items: PlayHistory[]): Map<string, DayData> {
  const dayMap = new Map<string, DayData>();

  items.forEach((item) => {
    const date = new Date(item.playedAt);
    const dateString = getLocalDateString(date); // Use local date, not UTC

    if (dayMap.has(dateString)) {
      const existing = dayMap.get(dateString)!;
      existing.count += 1;
    } else {
      // Create normalized date at midnight local time
      const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      dayMap.set(dateString, {
        date: normalizedDate,
        count: 1,
        dateString,
      });
    }
  });

  return dayMap;
}

/**
 * Generates a complete 30-day range with all days (even if no data)
 */
function generate30DayRange(startDate: Date, dayDataMap: Map<string, DayData>): DayData[] {
  const days: DayData[] = [];

  for (let i = 0; i < 30; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() - i);
    const dateString = getLocalDateString(date); // Use local date, not UTC

    const existingData = dayDataMap.get(dateString);
    if (existingData) {
      days.push(existingData);
    } else {
      // Create date at midnight local time
      const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      days.push({ date: normalizedDate, count: 0, dateString });
    }
  }

  return days.reverse(); // Oldest first
}

export function HistoryTimeline({ items, onDateSelect, selectedDate }: HistoryTimelineProps) {
  // Track which 30-day period we're viewing (offset from today)
  const [periodOffset, setPeriodOffset] = useState(0); // 0 = last 30 days, 1 = 30-60 days ago, etc.

  // Group items by day
  const dayDataMap = useMemo(() => groupByDay(items), [items]);

  // Calculate date range for current period
  const { startDate, endDate, days } = useMemo(() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    end.setDate(end.getDate() - periodOffset * 30);

    const start = new Date(end);
    start.setDate(start.getDate() - 29); // 30 days including end date

    const daysInPeriod = generate30DayRange(end, dayDataMap);

    return { startDate: start, endDate: end, days: daysInPeriod };
  }, [periodOffset, dayDataMap]);

  // Calculate max count for scaling bars
  const maxCount = useMemo(() => {
    return Math.max(...days.map((d) => d.count), 1);
  }, [days]);

  // Format date range label
  const dateRangeLabel = useMemo(() => {
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  }, [startDate, endDate]);

  // Handle day click
  const handleDayClick = (day: DayData) => {
    if (day.count === 0) return; // Don't allow selecting days with no data

    // Toggle selection: if clicking the same day, deselect
    if (selectedDate && getLocalDateString(selectedDate) === day.dateString) {
      onDateSelect(null);
    } else {
      onDateSelect(day.date);
    }
  };

  // Navigation handlers
  const goToPreviousPeriod = () => setPeriodOffset((prev) => prev + 1);
  const goToNextPeriod = () => setPeriodOffset((prev) => Math.max(0, prev - 1));
  const goToToday = () => {
    setPeriodOffset(0);
    onDateSelect(null);
  };

  const isCurrentPeriod = periodOffset === 0;

  return (
    <div className="bg-zinc-900 border-b border-zinc-800 p-4">
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-zinc-500" />
          <span className="text-sm font-medium text-zinc-300">{dateRangeLabel}</span>
          {selectedDate && (
            <span className="text-xs text-emerald-500 ml-2">
              • Filtered to {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Clear filter button */}
          {selectedDate && (
            <button
              onClick={() => onDateSelect(null)}
              className="px-2 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-200 transition-colors"
              title="Show all dates"
            >
              Clear filter
            </button>
          )}

          {/* Navigation buttons */}
          <button
            onClick={goToPreviousPeriod}
            className="p-1 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors"
            title="Previous 30 days"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {!isCurrentPeriod && (
            <button
              onClick={goToToday}
              className="px-2 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
            >
              Today
            </button>
          )}

          <button
            onClick={goToNextPeriod}
            disabled={isCurrentPeriod}
            className={`p-1 rounded transition-colors ${
              isCurrentPeriod
                ? 'text-zinc-700 cursor-not-allowed'
                : 'hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200'
            }`}
            title="Next 30 days"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Timeline bars */}
      <div className="flex items-end gap-[2px] h-20">
        {days.map((day) => {
          // Better scaling for small values: use sqrt to make small values more visible
          const scaledHeight = maxCount > 0 ? Math.sqrt(day.count / maxCount) * 100 : 0;
          const isSelected =
            selectedDate && getLocalDateString(selectedDate) === day.dateString;
          const hasData = day.count > 0;

          return (
            <div
              key={day.dateString}
              onClick={() => handleDayClick(day)}
              className={`flex-1 flex flex-col items-center justify-end group relative transition-all ${
                hasData ? 'cursor-pointer' : 'cursor-default'
              }`}
              style={{ minWidth: '8px' }}
            >
              {/* Bar */}
              <div
                className={`w-full rounded-t transition-all ${
                  isSelected
                    ? 'bg-emerald-500'
                    : hasData
                    ? 'bg-zinc-600 group-hover:bg-emerald-400'
                    : 'bg-zinc-800/50'
                }`}
                style={{
                  height: `${scaledHeight}%`,
                  minHeight: hasData ? '12px' : '2px' // Much taller minimum for visibility
                }}
              />

              {/* Tooltip on hover */}
              {hasData && (
                <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 pointer-events-none">
                  <div className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-xs whitespace-nowrap shadow-lg">
                    <div className="text-zinc-300 font-medium">
                      {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="text-emerald-400">
                      {day.count} {day.count === 1 ? 'track' : 'tracks'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Date labels (show every 5 days) */}
      <div className="flex items-center gap-[2px] mt-1">
        {days.map((day, index) => {
          const showLabel = index % 5 === 0 || index === days.length - 1;
          return (
            <div
              key={`label-${day.dateString}`}
              className="flex-1 text-[9px] text-zinc-600 text-center"
              style={{ minWidth: '4px' }}
            >
              {showLabel && day.date.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
